import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Cache en mémoire pour les données prefetchées
const prefetchCache = new Map();
const prefetchInProgress = new Set();

/**
 * Prefetch des données et les stocker dans le cache localStorage
 */
export const prefetchData = async (cacheKey, fetchFn) => {
  // Éviter les requêtes en doublon
  if (prefetchInProgress.has(cacheKey)) {
    return;
  }

  // Vérifier si le cache est encore frais (< 1 minute)
  try {
    const cached = localStorage.getItem(`cache_${cacheKey}`);
    if (cached) {
      const { timestamp } = JSON.parse(cached);
      const age = Date.now() - timestamp;
      if (age < 60 * 1000) { // Cache de moins d'1 minute, pas besoin de prefetch
        return;
      }
    }
  } catch (e) {
    // Ignorer les erreurs de lecture du cache
  }

  prefetchInProgress.add(cacheKey);

  try {
    const data = await fetchFn();
    
    // Sauvegarder dans localStorage
    const cacheEntry = {
      data,
      timestamp: Date.now()
    };
    localStorage.setItem(`cache_${cacheKey}`, JSON.stringify(cacheEntry));
    
    // Aussi garder en mémoire pour accès ultra-rapide
    prefetchCache.set(cacheKey, cacheEntry);
    
    console.log(`[Prefetch] ${cacheKey} préchargé avec succès`);
  } catch (error) {
    console.warn(`[Prefetch] Erreur pour ${cacheKey}:`, error.message);
  } finally {
    prefetchInProgress.delete(cacheKey);
  }
};

/**
 * Prefetch des missions du planning
 */
export const prefetchPlanning = () => {
  return prefetchData('planning_missions', async () => {
    const response = await axios.get(`${API}/missions`);
    return response.data;
  });
};

/**
 * Prefetch des données du Dashboard
 */
export const prefetchDashboard = () => {
  return prefetchData('dashboard_data', async () => {
    const [statsRes, alertsRes] = await Promise.all([
      axios.get(`${API}/stats/overview`).catch(() => ({ data: null })),
      axios.get(`${API}/missions/last-minute-alerts`).catch(() => ({ data: [] }))
    ]);
    return {
      stats: statsRes.data,
      alerts: alertsRes.data
    };
  });
};

/**
 * Prefetch des données du Bilan pour le mois courant
 */
export const prefetchBilan = () => {
  const now = new Date();
  const year = now.getFullYear();
  const month = now.getMonth() + 1;
  
  return prefetchData(`bilan_${year}_${month}`, async () => {
    const startDate = `${year}-${String(month).padStart(2, '0')}-01`;
    const lastDay = new Date(year, month, 0).getDate();
    const endDate = `${year}-${String(month).padStart(2, '0')}-${lastDay}`;
    
    const response = await axios.get(`${API}/bilan?start_date=${startDate}&end_date=${endDate}`);
    return response.data;
  });
};

/**
 * Prefetch des données de Facturation pour le mois courant
 */
export const prefetchFacturation = () => {
  const now = new Date();
  const year = now.getFullYear();
  const month = now.getMonth() + 1;
  
  return prefetchData(`facturation_${year}_${month}`, async () => {
    const response = await axios.get(`${API}/facturation/monthly-recap/${year}/${month}`);
    return response.data;
  });
};

/**
 * Prefetch toutes les données principales (appelé après connexion)
 */
export const prefetchAll = async () => {
  // Utiliser requestIdleCallback si disponible, sinon setTimeout
  const scheduleIdle = window.requestIdleCallback || ((cb) => setTimeout(cb, 100));
  
  scheduleIdle(() => {
    prefetchPlanning();
  });
  
  scheduleIdle(() => {
    prefetchDashboard();
  });
};

/**
 * Hook pour prefetch au hover
 * Usage: onMouseEnter={() => prefetchOnHover('planning')}
 */
export const prefetchOnHover = (page) => {
  switch (page) {
    case 'planning':
      prefetchPlanning();
      break;
    case 'dashboard':
      prefetchDashboard();
      break;
    case 'bilan':
      prefetchBilan();
      break;
    case 'facturation':
      prefetchFacturation();
      break;
    default:
      break;
  }
};

export default {
  prefetchData,
  prefetchPlanning,
  prefetchDashboard,
  prefetchBilan,
  prefetchFacturation,
  prefetchAll,
  prefetchOnHover
};
